import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPlay, FaPause } from "react-icons/fa";

// Module-level ref: only one audio element plays at a time
let currentAudioElement = null;

const formatAudioTime = (time) => {
    if (!isFinite(time) || isNaN(time) || time <= 0) return "0:00";
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
};

const AudioPlayer = ({ src, isFromCurrentUser }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    // iOS detection
    const isIOS = useMemo(() => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    }, []);

    // Reset when src changes - iOS compatible
    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;

        setIsPlaying(false);
        setCurrentTime(0);
        setIsLoading(false);

        // iOS: Don't preload automatically
        if (isIOS) {
            a.load();
        } else {
            a.load();
        }
    }, [src, isIOS]);

    // Wire up audio events with iOS considerations
    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;

        const updateDuration = () => {
            if (isFinite(a.duration) && !isNaN(a.duration) && a.duration > 0) {
                setDuration(a.duration);
            }
        };

        const onLoadedMetadata = () => {
            updateDuration();
            setIsLoading(false);
        };

        const onLoadStart = () => {
            setIsLoading(true);
        };

        const onCanPlay = () => {
            setIsLoading(false);
            updateDuration();
        };

        const onTimeUpdate = () => {
            setCurrentTime(a.currentTime);
        };

        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(a.duration || 0);
            if (currentAudioElement === a) currentAudioElement = null;
        };

        const onPlay = () => {
            setIsPlaying(true);
            setHasInteracted(true); // Mark that user has interacted
        };

        const onPause = () => {
            setIsPlaying(false);
        };

        const onError = (e) => {
            console.error("Audio error:", e);
            setIsLoading(false);
            setIsPlaying(false);

            // iOS-specific error handling
            if (isIOS) {
                console.log("iOS audio error - trying to handle...");
            }
        };

        // Add event listeners
        a.addEventListener("loadedmetadata", onLoadedMetadata);
        a.addEventListener("loadstart", onLoadStart);
        a.addEventListener("canplay", onCanPlay);
        a.addEventListener("timeupdate", onTimeUpdate);
        a.addEventListener("ended", onEnded);
        a.addEventListener("play", onPlay);
        a.addEventListener("pause", onPause);
        a.addEventListener("error", onError);

        // iOS-specific: Try to get duration
        if (isIOS) {
            const tryGetDuration = () => {
                if (a.duration && isFinite(a.duration)) {
                    setDuration(a.duration);
                }
            };
            a.addEventListener("loadeddata", tryGetDuration);

            return () => {
                a.removeEventListener("loadeddata", tryGetDuration);
                a.removeEventListener("loadedmetadata", onLoadedMetadata);
                a.removeEventListener("loadstart", onLoadStart);
                a.removeEventListener("canplay", onCanPlay);
                a.removeEventListener("timeupdate", onTimeUpdate);
                a.removeEventListener("ended", onEnded);
                a.removeEventListener("play", onPlay);
                a.removeEventListener("pause", onPause);
                a.removeEventListener("error", onError);
            };
        }

        return () => {
            a.removeEventListener("loadedmetadata", onLoadedMetadata);
            a.removeEventListener("loadstart", onLoadStart);
            a.removeEventListener("canplay", onCanPlay);
            a.removeEventListener("timeupdate", onTimeUpdate);
            a.removeEventListener("ended", onEnded);
            a.removeEventListener("play", onPlay);
            a.removeEventListener("pause", onPause);
            a.removeEventListener("error", onError);
        };
    }, [isIOS]);

    const togglePlayPause = async () => {
        const a = audioRef.current;
        if (!a) return;

        try {
            if (a.paused) {
                // Stop any currently playing audio element
                if (currentAudioElement && currentAudioElement !== a) {
                    currentAudioElement.pause();
                }
                currentAudioElement = a;
                // iOS requires this to be in a user gesture handler
                await a.play();
                setIsPlaying(true);
            } else {
                a.pause();
                if (currentAudioElement === a) currentAudioElement = null;
                setIsPlaying(false);
            }
        } catch (error) {
            console.error("Playback error:", error);
            setIsPlaying(false);

            // iOS-specific error handling
            if (isIOS) {
                // Sometimes iOS needs the audio to be loaded again
                a.load();
                setTimeout(async () => {
                    try {
                        await a.play();
                        setIsPlaying(true);
                    } catch (retryError) {
                        console.error("Retry failed:", retryError);
                    }
                }, 100);
            }
        }
    };

    const totalBars = 30;

    const bars = useMemo(() => {
        return Array.from({ length: totalBars }, (_, i) => {
            const height = (Math.sin((i / totalBars) * Math.PI * 2) * 0.3 + 0.7) * 14 + 3;
            return { height };
        });
    }, [totalBars]);

    const activeBarIndex = duration > 0
        ? Math.min(Math.floor((currentTime / duration) * totalBars), totalBars - 1)
        : -1;

    const waveformBars = bars.map((bar, i) => {
        const isActive = currentTime > 0 && i <= activeBarIndex;
        const activeColor = isFromCurrentUser ? "bg-white" : "bg-primary";

        return (
            <div
                key={i}
                style={{ height: `${bar.height}px` }}
                className={`w-[2.5px] rounded-full transition-colors duration-150 ${isActive ? activeColor : "bg-gray-400"}`}
            />
        );
    });

    return (
        <div
            className={`flex items-center gap-3 p-2 rounded-xl ${isFromCurrentUser ? "bg-primary text-white" : "bg-[#F2F7FB] text-black"
                }`}
            style={{
                minWidth: "220px",
                maxWidth: "280px",
                overflow: "hidden"
            }}
        >
            {/* iOS-compatible audio element */}
            <audio
                ref={audioRef}
                preload={isIOS ? "metadata" : "auto"} // iOS restricts preload="auto"
                crossOrigin="anonymous"
                playsInline // Essential for iOS
                webkit-playsinline="true" // For older iOS WebKit
                controls={false} // We're using custom controls
            >
                <source src={src} type="audio/mp4" />
                <source src={src} type="audio/aac" />
                <source src={src} type="audio/mpeg" />
                <source src={src} type="audio/webm" />
                Your browser does not support the audio element.
            </audio>

            <button
                onClick={togglePlayPause}
                className="flex-shrink-0 focus:outline-none"
                disabled={isLoading}
            >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shadow-md ${isLoading ? "bg-gray-300" : "bg-white"
                    }`}>
                    {isLoading ? (
                        <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : isPlaying ? (
                        <FaPause size={12} className="text-primary" />
                    ) : (
                        <FaPlay size={12} className="text-primary pl-0.5" />
                    )}
                </div>
            </button>

            <div className="flex-1 flex flex-col justify-center min-w-0">
                {/* Waveform container */}
                <div className="flex items-center justify-between gap-1 h-7">
                    {waveformBars}
                </div>

                {/* Times with loading state */}
                <div className="flex justify-between text-xs mt-0.5 font-mono">
                    <span className={isFromCurrentUser ? "text-gray-200" : "text-gray-600"}>
                        {formatAudioTime(currentTime)}
                    </span>
                    <span className={isFromCurrentUser ? "text-gray-200" : "text-gray-600"}>
                        {isLoading ? "..." : formatAudioTime(duration)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default AudioPlayer;